import React, { useEffect, useMemo, useState } from 'react';
import { useScroll, useTransform, motion, useReducedMotion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { client } from '../lib/sanity';
import ScrollMore from '../components/ScrollMore';
import Button from '../components/ui/Button';

const useTypewriterEffect = (phrases) => {
    const [state, setState] = useState({
        displayText: '',
        phraseIndex: 0,
        charIndex: 0,
        isDeleting: false,
    });

    useEffect(() => {
        if (!phrases?.length) return;

        const { phraseIndex, charIndex, isDeleting } = state;
        const currentPhrase = phrases[phraseIndex];
        const typeSpeed = isDeleting ? 40 : 70;
        const pauseEnd = 1200;
        const pauseStart = 250;

        let timer;
        const nextState = { ...state };

        if (!isDeleting && charIndex < currentPhrase.length) {
            timer = setTimeout(() => {
                nextState.charIndex = charIndex + 1;
                nextState.displayText = currentPhrase.slice(0, nextState.charIndex);
                setState(nextState);
            }, charIndex === 0 ? pauseStart : typeSpeed);
        } else if (!isDeleting && charIndex === currentPhrase.length) {
            timer = setTimeout(() => {
                nextState.isDeleting = true;
                setState(nextState);
            }, pauseEnd);
        } else if (isDeleting && charIndex > 0) {
            timer = setTimeout(() => {
                nextState.charIndex = charIndex - 1;
                nextState.displayText = currentPhrase.slice(0, nextState.charIndex);
                setState(nextState);
            }, typeSpeed);
        } else if (isDeleting && charIndex === 0) {
            timer = setTimeout(() => {
                nextState.isDeleting = false;
                nextState.phraseIndex = (phraseIndex + 1) % phrases.length;
                setState(nextState);
            }, pauseStart);
        }

        return () => clearTimeout(timer);
    }, [phrases, state]);

    return state.displayText;
};

export default function Home() {
    const [dataState, setDataState] = useState({
        heroData: null,
        loading: true,
        error: null,
    });

    const shouldReduceMotion = useReducedMotion();
    const { scrollY } = useScroll();
    const bgY = useTransform(scrollY, [0, 500], shouldReduceMotion ? [0, 0] : [0, 150]);
    const overlayOpacity = useTransform(scrollY, [0, 300], [0.4, 0.6]);

    useEffect(() => {
        const controller = new AbortController();

        client
            .fetch(
                `*[_type == "hero"][0]{ heading, highlight, tagline, "bgImage": background.asset->url }`,
                {},
                { signal: controller.signal }
            )
            .then((heroData) => {
                setDataState({ heroData, loading: false, error: null });
            })
            .catch((err) => {
                if (err.name !== 'AbortError') {
                    setDataState({ heroData: null, loading: false, error: err?.message || 'Failed to load content' });
                }
            });

        return () => controller.abort();
    }, []);

    const taglines = useMemo(() => {
        const t = dataState.heroData?.tagline;
        if (!t) return [];
        if (Array.isArray(t)) return t.filter(Boolean).map(s => String(s).trim()).filter(Boolean);
        if (typeof t === 'string') {
            const separators = ['|', '•', ','];
            const sep = separators.find(s => t.includes(s));
            return sep ? t.split(sep).map(s => s.trim()).filter(Boolean) : [t.trim()];
        }
        return [];
    }, [dataState.heroData]);

    const displayText = useTypewriterEffect(taglines);

    const { heroData, loading, error } = dataState;
    const hasBg = useMemo(() => Boolean(heroData?.bgImage), [heroData]);

    if (loading) {
        return (
            <section
                className="relative -mt-16 pt-16 min-h-[100svh] w-full flex items-end justify-center pb-6 sm:pb-8 md:pb-10 bg-white dark:bg-zinc-950"
                aria-label="Hero (loading)"
            >
                <div className="relative z-10 w-full max-w-4xl px-4 sm:px-6 lg:px-8">
                    <div className="mx-auto w-full border-2 border-black dark:border-white p-3 sm:p-6 md:p-4 animate-pulse">
                        <div className="h-10 sm:h-12 md:h-14 w-3/4 bg-zinc-200 dark:bg-zinc-800" />
                        <div className="mt-3 h-5 w-1/2 bg-zinc-200 dark:bg-zinc-800" />
                        <div className="mt-6 h-10 w-32 bg-accent/60" />
                    </div>
                </div>
            </section>
        );
    }

    if (error) {
        return (
            <section
                className="relative -mt-16 pt-16 min-h-[60svh] w-full flex items-center justify-center bg-white dark:bg-zinc-950"
                aria-label="Hero (error)"
            >
                <div className="text-center px-6">
                    <p className="text-red-600 dark:text-red-500 font-semibold">{error}</p>
                    <p className="mt-2 text-zinc-600 dark:text-zinc-300">Refresh the page to try again.</p>
                </div>
            </section>
        );
    }

    return (
        <>
            <section
                className="relative -mt-16 pt-16 min-h-[100svh] w-full flex items-center justify-start pb-6 sm:pb-8 md:pb-10 overflow-hidden"
                aria-label="Hero"
            >
                {/* Monochrome base + single static accent glow (no multi-hue gradient sweep) */}
                <div className="absolute inset-0 bg-zinc-950 -z-20" />
                <div
                    className="absolute inset-0 -z-10 opacity-40"
                    style={{ background: 'radial-gradient(ellipse at 20% 30%, rgba(255,95,31,0.25), transparent 60%)' }}
                />

                {/* Parallax background image */}
                {hasBg && (
                    <motion.div
                        style={{ y: bgY }}
                        className="absolute inset-0 -z-20"
                    >
                        <div
                            style={{ backgroundImage: `url(${heroData.bgImage})` }}
                            className="h-full w-full bg-cover bg-center bg-no-repeat"
                        />
                    </motion.div>
                )}

                {/* Overlays */}
                <div className="pointer-events-none absolute inset-0 -z-10">
                    <motion.div
                        style={{ opacity: overlayOpacity }}
                        className="h-full w-full bg-white/30 dark:bg-black/40 mix-blend-multiply"
                    />
                    <div className="absolute inset-0 bg-gradient-to-r from-black/15 to-transparent dark:from-black/40" />
                </div>

                {/* Content */}
                <div className="relative z-10 w-full max-w-5xl px-6 sm:px-10 md:px-14 lg:px-16 flex flex-col items-start">
                    {/* Heading - Fade in with stagger */}
                    <motion.h1
                        className="text-left text-[clamp(1.75rem,5.5vw,3.25rem)] md:text-5xl font-serif font-extrabold leading-tight text-white"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.1 }}
                    >
                        {heroData?.heading || 'Dristanta Silwal'}
                        <br className="hidden sm:block" />
                        <motion.span
                            className="block sm:inline text-[clamp(1rem,2.5vw,1.5rem)] md:text-2xl text-accent"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ duration: 0.8, delay: 0.3 }}
                        >
                            {heroData?.highlight}
                        </motion.span>
                    </motion.h1>

                    {/* Tagline - Typewriter effect with stagger */}
                    {taglines.length > 0 && (
                        <motion.p
                            className="mt-4 text-left text-[clamp(0.95rem,2.2vw,1.125rem)] text-zinc-100/90 font-medium tracking-wide"
                            aria-live="polite"
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 0.5 }}
                        >
                            <span className="whitespace-pre-wrap">
                                {displayText}
                            </span>
                            <span className="inline-block align-baseline ml-1 border-r-2 border-current animate-pulse" style={{ height: '1.2em', transform: 'translateY(2px)' }} />
                        </motion.p>
                    )}

                    {/* CTA Buttons - Staggered appearance */}
                    <motion.div
                        className="mt-8 flex flex-col sm:flex-row gap-4"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.7 }}
                    >
                        <Button as={Link} to="/projects" className="px-8 py-3">
                            View Projects
                        </Button>
                        <Button as={Link} to="/contact" variant="secondary" className="!bg-transparent !border-white !text-white hover:!bg-white hover:!text-black px-8 py-3">
                            Contact Me
                        </Button>
                    </motion.div>
                </div>

                {/* Scroll indicator - Bottom animation */}
                <div className="absolute bottom-6 sm:bottom-8 left-1/2 -translate-x-1/2 z-20">
                    <motion.div
                        className="flex flex-col items-center gap-2"
                        animate={shouldReduceMotion ? undefined : { y: [0, 12, 0] }}
                        transition={shouldReduceMotion ? undefined : { duration: 1.5, repeat: Infinity }}
                    >
                        <span className="text-sm text-white/60 dark:text-white/50 font-medium">Scroll to explore</span>
                        <svg
                            className="w-6 h-6 text-white/60 dark:text-white/50"
                            fill="none"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                        >
                            <path d="M19 14l-7 7m0 0l-7-7m7 7V3"></path>
                        </svg>
                    </motion.div>
                </div>
            </section>
            <ScrollMore />
        </>
    );
}